import { useState, useEffect, useRef } from "react";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { supabase } from "@/integrations/supabase/client";
import { useToast } from "@/hooks/use-toast";
import { Loader2 } from "lucide-react";
import { LogoUpload, uploadLogoForEntity } from "@/components/admin/LogoUpload";
import type { LogoPathArgs } from "@/lib/slugify";
import {
  SERVICE_MODEL_OPTIONS,
  questionnaireUsesServiceModels,
  serviceModelsRequired,
  validateServiceModels,
} from "@/lib/serviceModels";

type OrganizationLevel = "client" | "sub_org" | "portfolio";

interface Client {
  id: string;
  name: string;
}

interface SubOrg {
  id: string;
  name: string;
  client_id: string;
}

interface QuestionnaireOption {
  questionnaire_id: string;
  label: string;
}

interface CreateOrganizationWizardProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onCreated?: () => void;
  /** Pre-selected client context (when drilling into a client) */
  preselectedClientId?: string | null;
  preselectedClientName?: string | null;
  /** Pre-selected sub-org context (when drilling into a sub-org) */
  preselectedSubOrgId?: string | null;
  preselectedSubOrgName?: string | null;
}

const LEVEL_OPTIONS: { value: OrganizationLevel; label: string; description: string }[] = [
  { value: "client", label: "Client Organization", description: "Top-level organization (e.g., World Bank, IDB Invest)" },
  { value: "sub_org", label: "Sub-Organization", description: "Division within a client (e.g., TMT, Energy)" },
  { value: "portfolio", label: "Portfolio Company", description: "Individual company within a sub-org (e.g., Telecom Armenia)" },
];

const CATEGORY_OPTIONS: { value: string; label: string }[] = [
  { value: "Cybersecurity Starter Assessments", label: "Cybersecurity Starter Assessments" },
  { value: "Telecom (Fixed/ISP)", label: "Telecommunications" },
  { value: "Media", label: "Media" },
  { value: "Private Equity", label: "Private Equity" },
  { value: "Data Center", label: "Data Center" },
  { value: "Gen AI", label: "Gen AI" },
  { value: "Risk Exposure", label: "Risk Exposure" },
  { value: "Pre-Investment Triage", label: "Pre-Investment Triage" },
];

// Phase 1 (Service Model Foundation) — the option list and the telecom gate now
// come from a single shared source of truth shared with the server allow-list.
const TELECOM_SERVICE_MODEL_OPTIONS = SERVICE_MODEL_OPTIONS;

// The portfolio is created with `questionnaireId || "telecom_v2"`, so the gate
// must evaluate the SAME effective id — otherwise service models are silently
// skipped for portfolios that end up as telecom_v2 via the fallback.
function effectiveQuestionnaireId(questionnaireId: string): string {
  return questionnaireId || "telecom_v2";
}

function isTelecomQuestionnaire(_category: string, questionnaireId: string): boolean {
  return questionnaireUsesServiceModels(effectiveQuestionnaireId(questionnaireId));
}


export const CreateOrganizationWizard = ({
  open,
  onOpenChange,
  onCreated,
  preselectedClientId,
  preselectedClientName,
  preselectedSubOrgId,
  preselectedSubOrgName,
}: CreateOrganizationWizardProps) => {
  // Determine default level based on context
  const getDefaultLevel = (): OrganizationLevel => {
    if (preselectedSubOrgId) return "portfolio";
    if (preselectedClientId) return "sub_org";
    return "client";
  };

  const [level, setLevel] = useState<OrganizationLevel>(getDefaultLevel());
  const [name, setName] = useState("");
  const [selectedClientId, setSelectedClientId] = useState<string | null>(preselectedClientId ?? null);
  const [selectedSubOrgId, setSelectedSubOrgId] = useState<string | null>(preselectedSubOrgId ?? null);
  const [category, setCategory] = useState("");
  const [questionnaireId, setQuestionnaireId] = useState("");
  const [serviceModels, setServiceModels] = useState<string[]>([]);
  const [serviceModelOther, setServiceModelOther] = useState("");
  const requestKeyRef = useRef<string | null>(null);
  const [logoPath, setLogoPath] = useState("");
  const [pendingLogoFile, setPendingLogoFile] = useState<File | null>(null);

  const [clients, setClients] = useState<Client[]>([]);
  const [subOrgs, setSubOrgs] = useState<SubOrg[]>([]);
  const [questionnaireOptions, setQuestionnaireOptions] = useState<QuestionnaireOption[]>([]);

  const [isLoading, setIsLoading] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const { toast } = useToast();

  // Determine available levels based on context
  const availableLevels = LEVEL_OPTIONS.filter((opt) => {
    if (preselectedSubOrgId) return opt.value === "portfolio";
    if (preselectedClientId) return opt.value === "sub_org" || opt.value === "portfolio";
    return true;
  });

  // Reset form when modal opens
  useEffect(() => {
    if (open) {
      const defaultLevel = getDefaultLevel();
      setLevel(defaultLevel);
      setName("");
      setSelectedClientId(preselectedClientId ?? null);
      setSelectedSubOrgId(preselectedSubOrgId ?? null);
      setCategory("");
      setQuestionnaireId("");
      setServiceModels([]);
      setLogoPath("");
      setPendingLogoFile(null);
      // Only load clients if we don't have a preselected one
      if (!preselectedClientId) {
        loadClients();
      } else if (preselectedClientId && !preselectedSubOrgId) {
        loadSubOrgs(preselectedClientId);
      }
    }
  }, [open, preselectedClientId, preselectedSubOrgId]);

  // Load sub-orgs when client changes
  useEffect(() => {
    if (selectedClientId) {
      loadSubOrgs(selectedClientId);
    } else {
      setSubOrgs([]);
      setSelectedSubOrgId(null);
    }
  }, [selectedClientId]);

  // Load questionnaire options when category changes (for portfolio level)
  useEffect(() => {
    if (level === "portfolio" && category) {
      loadQuestionnaireOptions(category);
    } else {
      setQuestionnaireOptions([]);
      setQuestionnaireId("");
    }
    // Clear telecom service models whenever the category changes — they only
    // apply when the resulting questionnaire is a Telecom CSF 2.0 one.
    setServiceModels([]);
  }, [category, level]);

  // Clear service models whenever the questionnaire is not telecom_v2.
  useEffect(() => {
    if (!isTelecomQuestionnaire(category, questionnaireId)) {
      setServiceModels([]);
    }
  }, [questionnaireId, category]);

  const loadClients = async () => {
    setIsLoading(true);
    try {
      const { data, error } = await supabase.rpc("admin_list_clients");

      if (error) throw error;
      // RPC returns rows with id, name, created_at - map to Client shape
      setClients((data || []).map((c: { id: string; name: string }) => ({ id: c.id, name: c.name })));
    } catch (error) {
      console.error("Error loading clients:", error);
      toast({
        title: "Error",
        description: "Failed to load clients",
        variant: "destructive",
      });
    } finally {
      setIsLoading(false);
    }
  };

  const loadSubOrgs = async (clientId: string) => {
    try {
      // Use admin RPC to bypass RLS - no table fallback
      const { data, error } = await (supabase.rpc as any)("admin_list_sub_orgs", {
        p_client_id: clientId,
      });

      if (error) {
        if (error.code === "PGRST202") {
          throw new Error("DB migration missing admin_list_sub_orgs()");
        }
        throw error;
      }
      setSubOrgs((data || []) as SubOrg[]);
    } catch (error) {
      console.error("Error loading sub-organizations:", error);
    }
  };

  const loadQuestionnaireOptions = async (cat: string) => {
    try {
      const { data, error } = await supabase
        .from("v_org_category_questionnaires")
        .select("questionnaire_id, label")
        .eq("category", cat);

      if (error) throw error;
      setQuestionnaireOptions(data || []);

      // Default to telecom_v2 if available, then first option
      const defaultOption =
        (data || []).find((d) => d.questionnaire_id === "telecom_v2")?.questionnaire_id ||
        (data && data[0]?.questionnaire_id) ||
        "";
      setQuestionnaireId(defaultOption);
    } catch (error) {
      console.error("Error loading questionnaires:", error);
    }
  };

  const canSubmit = () => {
    if (!name.trim()) return false;

    switch (level) {
      case "client":
        return true;
      case "sub_org":
        return !!selectedClientId;
      case "portfolio":
        if (!selectedClientId || !selectedSubOrgId || !category) return false;
        if (category === "Telecom (Fixed/ISP)" && !questionnaireId) return false;
        // Phase 2.9.1 — Service Model is required when the questionnaire is a
        // Telecom CSF 2.0 (or any telecom_*) questionnaire.
        if (
          serviceModelsRequired(effectiveQuestionnaireId(questionnaireId)) &&
          serviceModels.length === 0
        )
          return false;
        return true;
      default:
        return false;
    }
  };

  const handleSubmit = async () => {
    if (!canSubmit()) return;

    // Debug logging before submit
    console.log("[CreateOrganizationWizard] Submit:", {
      level,
      name: name.trim(),
      selectedClientId,
      selectedSubOrgId,
      category,
      questionnaireId,
    });

    setIsSubmitting(true);
    try {
      switch (level) {
        case "client": {
          console.log("[CreateOrganizationWizard] Calling admin_create_client RPC with p_name:", name.trim());
          const { data: rpcData, error: rpcError } = await supabase.rpc("admin_create_client", {
            p_name: name.trim(),
          });
          console.log("[CreateOrganizationWizard] RPC response:", { data: rpcData, error: rpcError });

          if (rpcError) {
            if (rpcError.code === "PGRST202") {
              throw new Error("DB migration missing admin_create_client()");
            }
            throw rpcError;
          }

          // Upload logo — edge function handles both storage upload and DB update
          if (pendingLogoFile && rpcData) {
            const pathArgs: LogoPathArgs = { level: "client", clientName: name.trim(), entityId: rpcData };
            await uploadLogoForEntity(pendingLogoFile, pathArgs);
          }

          console.log("[CreateOrganizationWizard] Client created via RPC, id:", rpcData);
          toast({ title: "Success", description: "Client organization created" });
          break;
        }
        case "sub_org": {
          const clientName = preselectedClientName || clients.find(c => c.id === selectedClientId)?.name || "";
          console.log("[CreateOrganizationWizard] Calling admin_create_sub_org RPC:", {
            p_client_id: selectedClientId,
            p_name: name.trim(),
          });
          const { data: rpcData, error: rpcError } = await (supabase.rpc as any)("admin_create_sub_org", {
            p_client_id: selectedClientId,
            p_name: name.trim(),
          });
          console.log("[CreateOrganizationWizard] RPC response:", { data: rpcData, error: rpcError });

          if (rpcError) {
            if (rpcError.code === "PGRST202") {
              throw new Error("DB migration missing admin_create_sub_org()");
            }
            throw rpcError;
          }

          if (pendingLogoFile && rpcData) {
            const pathArgs: LogoPathArgs = { level: "sub_org", clientName, subOrgName: name.trim(), entityId: rpcData };
            await uploadLogoForEntity(pendingLogoFile, pathArgs);
          }

          console.log("[CreateOrganizationWizard] Sub-org created via RPC, id:", rpcData);
          toast({ title: "Success", description: "Sub-organization created" });
          break;
        }
        case "portfolio": {
          const clientName = preselectedClientName || clients.find(c => c.id === selectedClientId)?.name || "";
          const subOrgName = preselectedSubOrgName || subOrgs.find(s => s.id === selectedSubOrgId)?.name || "";

          const effectiveQid = effectiveQuestionnaireId(questionnaireId);
          const usesModels = questionnaireUsesServiceModels(effectiveQid);

          // Phase 1 (revised) — validate before creating anything.
          if (usesModels) {
            const validation = validateServiceModels(serviceModels, {
              required: serviceModelsRequired(effectiveQid),
              otherDescription: serviceModelOther,
            });
            if (!validation.ok) throw new Error(validation.error);
          }

          // Phase 1 (revised) — the portfolio and its Service Models are created
          // by ONE transactional RPC, so a Service Model failure can no longer
          // leave an orphan portfolio behind. Retrying reuses the request key and
          // returns the existing portfolio instead of creating a duplicate.
          if (!requestKeyRef.current) requestKeyRef.current = crypto.randomUUID();

          const { data: rpcData, error: rpcError } = await (supabase.rpc as any)(
            "pm_create_portfolio_with_service_models",
            {
              p_sub_org_id: selectedSubOrgId,
              p_name: name.trim(),
              p_category: category,
              p_questionnaire_id: effectiveQid,
              p_models: usesModels ? serviceModels : null,
              p_other_description: serviceModelOther || null,
              p_request_key: requestKeyRef.current,
            },
          );

          if (rpcError) {
            if (rpcError.code === "PGRST202") {
              throw new Error("DB migration missing pm_create_portfolio_with_service_models()");
            }
            throw rpcError;
          }

          requestKeyRef.current = null;

          if (pendingLogoFile && rpcData) {
            const pathArgs: LogoPathArgs = {
              level: "portfolio",
              clientName,
              subOrgName,
              companyName: name.trim(),
              entityId: rpcData,
            };
            await uploadLogoForEntity(pendingLogoFile, pathArgs);
          }

          console.log("[CreateOrganizationWizard] Portfolio created via RPC, id:", rpcData);
          toast({ title: "Success", description: "Portfolio company created" });
          break;
        }
      }

      onOpenChange(false);
      onCreated?.();
    } catch (error: any) {
      console.error("Error creating organization:", error);
      const msg = error?.message || "";
      const isTierLimit = msg.includes("tier_limit_reached");
      toast({
        title: isTierLimit ? "Limit Reached" : "Error",
        description: isTierLimit
          ? "Tier 1 allows up to 3 portfolio companies. Upgrade to add more."
          : msg || "Failed to create organization",
        variant: "destructive",
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleLevelChange = (value: OrganizationLevel) => {
    setLevel(value);
    // Reset dependent fields
    setName("");
    setSelectedClientId(null);
    setSelectedSubOrgId(null);
    setCategory("");
    setQuestionnaireId("");
    setServiceModels([]);
    setLogoPath("");
    setPendingLogoFile(null);
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-md">
        <DialogHeader>
          <DialogTitle>Create Organization</DialogTitle>
        </DialogHeader>

        {isLoading ? (
          <div className="flex items-center justify-center py-8">
            <Loader2 className="h-6 w-6 animate-spin" />
          </div>
        ) : (
          <div className="space-y-4">
            {/* Level Selection */}
            <div className="space-y-2">
              <Label>Organization Level *</Label>
              {availableLevels.length === 1 ? (
                <p className="text-sm font-medium py-2">{availableLevels[0].label}</p>
              ) : (
                <Select value={level} onValueChange={(v) => handleLevelChange(v as OrganizationLevel)}>
                  <SelectTrigger>
                    <SelectValue placeholder="Select level" />
                  </SelectTrigger>
                  <SelectContent>
                    {availableLevels.map((opt) => (
                      <SelectItem key={opt.value} value={opt.value}>
                        {opt.label}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              )}
              <p className="text-xs text-muted-foreground">
                {LEVEL_OPTIONS.find((o) => o.value === level)?.description}
              </p>
            </div>

            {/* Parent Client (shown as read-only if preselected) */}
            {(level === "sub_org" || level === "portfolio") && preselectedClientId && (
              <div className="space-y-2">
                <Label>Parent Client</Label>
                <p className="text-sm font-medium py-2 px-3 bg-muted rounded-md">
                  {preselectedClientName || "Selected Client"}
                </p>
              </div>
            )}

            {/* Client Selection (only if not preselected) */}
            {(level === "sub_org" || level === "portfolio") && !preselectedClientId && (
              <div className="space-y-2">
                <Label>Parent Client *</Label>
                <Select
                  value={selectedClientId ?? undefined}
                  onValueChange={(v) => setSelectedClientId(v || null)}
                >
                  <SelectTrigger>
                    <SelectValue placeholder="Select client organization" />
                  </SelectTrigger>
                  <SelectContent>
                    {clients.map((client) => (
                      <SelectItem key={client.id} value={client.id}>
                        {client.name}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                {!selectedClientId && (
                  <p className="text-xs text-destructive">Please select a parent client</p>
                )}
              </div>
            )}

            {/* Parent Sub-Org (shown as read-only if preselected) */}
            {level === "portfolio" && preselectedSubOrgId && (
              <div className="space-y-2">
                <Label>Parent Sub-Organization</Label>
                <p className="text-sm font-medium py-2 px-3 bg-muted rounded-md">
                  {preselectedSubOrgName || "Selected Sub-Organization"}
                </p>
              </div>
            )}

            {/* Sub-Org Selection (only if not preselected) */}
            {level === "portfolio" && selectedClientId && !preselectedSubOrgId && (
              <div className="space-y-2">
                <Label>Parent Sub-Organization *</Label>
                <Select
                  value={selectedSubOrgId ?? undefined}
                  onValueChange={(v) => setSelectedSubOrgId(v || null)}
                >
                  <SelectTrigger>
                    <SelectValue placeholder="Select sub-organization" />
                  </SelectTrigger>
                  <SelectContent>
                    {subOrgs.length === 0 ? (
                      <SelectItem value="_none" disabled>
                        No sub-organizations found
                      </SelectItem>
                    ) : (
                      subOrgs.map((subOrg) => (
                        <SelectItem key={subOrg.id} value={subOrg.id}>
                          {subOrg.name}
                        </SelectItem>
                      ))
                    )}
                  </SelectContent>
                </Select>
                {!selectedSubOrgId && subOrgs.length > 0 && (
                  <p className="text-xs text-destructive">Please select a parent sub-organization</p>
                )}
                {subOrgs.length === 0 && (
                  <p className="text-xs text-muted-foreground">
                    Create a sub-organization under this client first
                  </p>
                )}
              </div>
            )}

            {/* Name Input */}
            <div className="space-y-2">
              <Label htmlFor="orgName">
                {level === "client"
                  ? "Client Name"
                  : level === "sub_org"
                  ? "Sub-Organization Name"
                  : "Portfolio Company Name"}{" "}
                *
              </Label>
              <Input
                id="orgName"
                value={name}
                onChange={(e) => setName(e.target.value)}
                placeholder={
                  level === "client"
                    ? "e.g., World Bank"
                    : level === "sub_org"
                    ? "e.g., TMT Division"
                    : "e.g., Telecom Armenia"
                }
              />
            </div>

            {/* Logo Upload (deferred - file held until entity is created) */}
            <LogoUpload
              onUploaded={setLogoPath}
              onFileSelected={(file) => setPendingLogoFile(file)}
            />

            {/* Category (for portfolio level) */}
            {level === "portfolio" && (
              <div className="space-y-2">
                <Label>Category *</Label>
                <Select value={category} onValueChange={setCategory}>
                  <SelectTrigger>
                    <SelectValue placeholder="Select category" />
                  </SelectTrigger>
                  <SelectContent>
                    {CATEGORY_OPTIONS.map((cat) => (
                      <SelectItem key={cat.value} value={cat.value}>
                        {cat.label}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            )}

            {/* Questionnaire (for Telecom category) */}
            {level === "portfolio" && questionnaireOptions.length > 0 && (
              <div className="space-y-2">
                <Label>Questionnaire *</Label>
                <Select value={questionnaireId} onValueChange={setQuestionnaireId}>
                  <SelectTrigger>
                    <SelectValue placeholder="Select questionnaire" />
                  </SelectTrigger>
                  <SelectContent>
                    {questionnaireOptions.map((opt) => (
                      <SelectItem key={opt.questionnaire_id} value={opt.questionnaire_id}>
                        {opt.label}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            )}

            {/* Phase 2.9.1 — Telecom Service Models (only for telecom CSF 2.0 questionnaires) */}
            {level === "portfolio" && isTelecomQuestionnaire(category, questionnaireId) && (
              <div className="space-y-2">
                <Label>Telecom Service Model(s) *</Label>
                <p className="text-xs text-muted-foreground">
                  Select all that apply. This is stored on the Portfolio Company
                  and inherited by reviewers and respondents.
                </p>
                <div className="flex flex-wrap gap-1.5">
                  {TELECOM_SERVICE_MODEL_OPTIONS.map((m) => {
                    const active = serviceModels.includes(m);
                    return (
                      <button
                        key={m}
                        type="button"
                        onClick={() =>
                          setServiceModels((prev) =>
                            prev.includes(m) ? prev.filter((x) => x !== m) : [...prev, m],
                          )
                        }
                        className={`text-xs rounded-full border px-2.5 py-1 transition-colors ${
                          active
                            ? "bg-primary text-primary-foreground border-primary"
                            : "bg-background text-muted-foreground border-border hover:bg-muted"
                        }`}
                      >
                        {m}
                      </button>
                    );
                  })}
                </div>
                {serviceModels.includes("Other") && (
                  <Input
                    placeholder="Describe the operating model"
                    value={serviceModelOther}
                    onChange={(e) => setServiceModelOther(e.target.value)}
                  />
                )}
                {serviceModels.length === 0 &&
                  serviceModelsRequired(effectiveQuestionnaireId(questionnaireId)) && (
                    <p className="text-xs text-destructive">Select at least one service model.</p>
                  )}
              </div>
            )}
          </div>
        )}

        <div className="flex justify-end gap-2 pt-4">
          <Button variant="outline" onClick={() => onOpenChange(false)} disabled={isSubmitting}>
            Cancel
          </Button>
          <Button onClick={handleSubmit} disabled={!canSubmit() || isSubmitting}>
            {isSubmitting ? (
              <>
                <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                Creating...
              </>
            ) : (
              "Create"
            )}
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
};
